import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MapPin } from "lucide-react";
import { setPageMeta } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityJobsPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/public/cities/${slug}`)
      .then((res) => {
        setData(res.data);
        const { city, uf, jobs } = res.data;
        setPageMeta({
          title: `Vagas de Sangrador de Seringueira em ${city}-${uf} | HeveaTalent`,
          description: `${jobs.length} vaga(s) de sangrador de seringueira em ${city}-${uf}. Contato direto com a fazenda, grátis. Atualizado hoje no HeveaTalent.`,
          canonical: `https://heveatalent.com.br/vagas/${slug}`,
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <p className="text-green-800">Carregando vagas...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="city-not-found">
          <CardContent className="pt-8 pb-8">
            <p className="text-4xl mb-3">🌳</p>
            <h2 className="text-xl font-bold text-gray-900">Cidade não encontrada</h2>
            <Link to="/vagas">
              <Button className="mt-4 bg-green-700 hover:bg-green-800">Ver todas as cidades →</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { city, uf, jobs, nearby, updated_at } = data;
  const updatedStr = new Date(updated_at).toLocaleDateString("pt-BR");

  return (
    <div className="min-h-screen bg-green-50" data-testid="city-jobs-page">
      <header className="bg-green-900 text-white py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <Link to="/" className="font-bold text-lg shrink-0">🌳 HeveaTalent</Link>
          <Link to="/">
            <Button size="sm" variant="outline" className="bg-white text-green-900 hover:bg-green-100" data-testid="city-header-cta">
              Entrar / Cadastrar grátis
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-green-900" data-testid="city-page-title">
            Vagas de Sangrador de Seringueira em {city}-{uf}
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-testid="city-page-subtitle">
            Atualizado em {updatedStr} · {jobs.length} vaga{jobs.length !== 1 ? "s" : ""} ativa{jobs.length !== 1 ? "s" : ""} · contato direto com quem contrata
          </p>
        </div>

        {jobs.length === 0 ? (
          <Card data-testid="city-no-jobs">
            <CardContent className="pt-6 pb-6 text-center">
              <p className="text-3xl mb-2">🔎</p>
              <p className="font-semibold text-gray-900">Nenhuma vaga ativa em {city} neste momento</p>
              <p className="text-sm text-gray-600 mt-1">
                Novas vagas entram toda semana. Cadastre-se grátis para ser avisado — ou veja as cidades vizinhas abaixo.
              </p>
              <Link to="/">
                <Button className="mt-4 bg-green-700 hover:bg-green-800" data-testid="city-register-btn">
                  Criar perfil grátis →
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <Card key={j.id} data-testid={`city-job-${j.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {j.urgent && <Badge className="bg-red-100 text-red-700" data-testid={`city-urgent-${j.id}`}>🔥 URGENTE</Badge>}
                    <Badge className="bg-sky-100 text-sky-800">VAGA</Badge>
                    {j.confirmed_at && <Badge className="bg-green-100 text-green-700" data-testid={`city-confirmed-${j.id}`}>✅ Confirmada {new Date(j.confirmed_at).toLocaleDateString("pt-BR")}</Badge>}
                    {j.salary_range && <Badge variant="outline">💰 {j.salary_range}</Badge>}
                  </div>
                  <CardTitle className="text-lg mt-1">{j.title}</CardTitle>
                  {j.location && (
                    <p className="text-sm text-gray-600 flex items-center"><MapPin className="w-4 h-4 mr-1" />{j.location}</p>
                  )}
                </CardHeader>
                <CardContent>
                  {j.description && <p className="text-sm text-gray-700 line-clamp-3">{j.description}</p>}
                  <Link to={`/vaga/${j.id}`}>
                    <Button size="sm" className="mt-3 bg-green-700 hover:bg-green-800" data-testid={`city-job-open-${j.id}`}>
                      Ver vaga e contato →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {nearby && nearby.length > 0 && (
          <Card data-testid="city-nearby">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-semibold text-gray-900 mb-2">Veja também vagas perto de {city}:</p>
              <div className="flex flex-wrap gap-2">
                {nearby.map((c) => (
                  <Link key={c.slug} to={`/vagas/${c.slug}`}>
                    <Badge variant="outline" className="cursor-pointer hover:bg-green-50" data-testid={`nearby-${c.slug}`}>
                      📍 {c.city}-{c.uf}{c.jobs_count > 0 ? ` (${c.jobs_count})` : ""}
                    </Badge>
                  </Link>
                ))}
              </div>
              <Link to="/vagas" className="text-xs text-green-700 underline block mt-2" data-testid="city-all-cities-link">
                Ver todas as cidades →
              </Link>
            </CardContent>
          </Card>
        )}

        <Card className="bg-green-900 text-white" data-testid="city-employer-cta">
          <CardContent className="pt-5 pb-5 text-center">
            <p className="font-semibold text-white">🌳 Tem seringueira em {city} e precisa de sangrador?</p>
            <p className="text-sm text-green-200 mt-1">Divulgue sua vaga grátis — ela aparece aqui e no Balcão para centenas de profissionais.</p>
            <Link to="/balcao">
              <Button size="sm" className="mt-3 bg-white text-green-900 hover:bg-green-100" data-testid="city-balcao-btn">
                Divulgar vaga grátis →
              </Button>
            </Link>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 pb-6">
          HeveaTalent — o canal de comunicação da heveicultura · vagas, tendência de preço e clima
        </p>
      </main>
    </div>
  );
}
